import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, ListGroup, Table, Spinner, Alert, Row, Col, Badge, Form } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import api from './api';

// Renk paleti
const CHART_COLORS = {
  kilo: '#60a5fa',
  gogus: '#f472b6',
  bel: '#fbbf24',
  kalca: '#34d399',
  vki: '#a78bfa',
};

function DanisanDetay({ theme }) {
  const { danisanId } = useParams();
  const [danisan, setDanisan] = useState(null);
  const [takipGecmisi, setTakipGecmisi] = useState([]);
  const [randevular, setRandevular] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duzenlemeModu, setDuzenlemeModu] = useState(false);
  const [formData, setFormData] = useState({});
  const [kayitHatasi, setKayitHatasi] = useState('');
  const [kayitYukleniyor, setKayitYukleniyor] = useState(false);

  const verileriGetir = useCallback(async () => {
    try {
      const [danisanRes, takipRes, randevuRes] = await Promise.all([
        api.get(`/diyetisyen/danisanlar/${danisanId}`),
        api.get(`/diyetisyen/danisanlar/${danisanId}/takip`),
        api.get(`/diyetisyen/danisanlar/${danisanId}/randevular`)
      ]);
      const d = danisanRes.data;
      setDanisan(d);
      setFormData({ ad: d.ad, soyad: d.soyad, eposta: d.eposta || '', telefon: d.telefon || '', hedef_kilo: d.hedef_kilo || '' });
      setTakipGecmisi(takipRes.data);
      setRandevular(randevuRes.data);
    } catch (err) {
      setError("Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [danisanId]);

  const handleKaydet = async () => {
    setKayitYukleniyor(true);
    setKayitHatasi('');
    try {
      await api.put(`/diyetisyen/danisanlar/${danisanId}`, formData);
      await verileriGetir();
      setDuzenlemeModu(false);
    } catch (err) {
      setKayitHatasi('Güncelleme başarısız. Lütfen tekrar deneyin.');
    } finally {
      setKayitYukleniyor(false);
    }
  };

  const handleIptal = () => {
    setFormData({ ad: danisan.ad, soyad: danisan.soyad, eposta: danisan.eposta || '', telefon: danisan.telefon || '', hedef_kilo: danisan.hedef_kilo || '' });
    setKayitHatasi('');
    setDuzenlemeModu(false);
  };

  useEffect(() => { verileriGetir(); }, [verileriGetir]);

  // Grafik verileri
  const chartData = useMemo(() => {
    return takipGecmisi.map(t => ({
      tarih: new Date(t.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
      kilo: t.kilo,
      gogus: t.gogus_cevresi,
      bel: t.bel_cevresi,
      kalca: t.kalca_cevresi,
      vki: t.vki,
    }));
  }, [takipGecmisi]);

  // Son ve ilk ölçüm karşılaştırması
  const stats = useMemo(() => {
    if (takipGecmisi.length < 1) return null;
    const ilk = takipGecmisi[0];
    const son = takipGecmisi[takipGecmisi.length - 1];

    const diff = (field) => {
      if (son[field] == null || ilk[field] == null) return null;
      return (son[field] - ilk[field]).toFixed(1);
    };

    return {
      kilo: { son: son.kilo, fark: diff('kilo') },
      gogus: { son: son.gogus_cevresi, fark: diff('gogus_cevresi') },
      bel: { son: son.bel_cevresi, fark: diff('bel_cevresi') },
      kalca: { son: son.kalca_cevresi, fark: diff('kalca_cevresi') },
      vki: { son: son.vki, fark: diff('vki') },
    };
  }, [takipGecmisi]);

  const DiffBadge = ({ value, unit = '', reverse = false }) => {
    if (value == null) return <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>;
    const num = parseFloat(value);
    const isPositive = num > 0;
    const color = num === 0 ? 'var(--text-muted)' : (isPositive === reverse ? '#10b981' : '#ef4444');
    return (
      <span style={{ color, fontSize: '0.85rem', fontWeight: '600' }}>
        {isPositive ? '+' : ''}{value}{unit}
      </span>
    );
  };

  const StatCard = ({ icon, label, value, unit, diff, color, diffReverse }) => (
    <div className="p-3 rounded-3 h-100" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="material-symbols-outlined" style={{ color, fontSize: '1.3rem' }}>{icon}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--text-main)', lineHeight: 1 }}>
        {value != null ? value : '—'}
        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: 'var(--text-muted)', marginLeft: '4px' }}>{value != null ? unit : ''}</span>
      </div>
      {diff != null && (
        <div className="mt-1">
          <DiffBadge value={diff} unit={unit} reverse={diffReverse} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginLeft: '4px' }}>toplam değişim</span>
        </div>
      )}
    </div>
  );

  const customTooltipStyle = {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    padding: '10px',
    color: 'var(--text-main)',
  };

  if (loading) return <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'dark'} />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!danisan) return <Alert variant="warning">Danışan bilgileri bulunamadı.</Alert>;

  return (
    <div>
      <Link to="/danisanlar" className="btn btn-sm d-inline-flex align-items-center gap-2 mb-4" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-color)', backgroundColor: 'transparent' }}>
        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span> Danışan Listesi
      </Link>
      
      {/* HEADER */}
      <div className="mb-4 p-5 d-flex align-items-center gap-4 flex-wrap" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1.25rem', boxShadow: 'var(--card-shadow)' }}>
        <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--accent-primary)', fontSize: '2rem', fontWeight: 'bold', flexShrink: 0 }}>
          {danisan.ad.charAt(0)}{danisan.soyad.charAt(0)}
        </div>

        {duzenlemeModu ? (
          <div className="flex-grow-1">
            {kayitHatasi && <Alert variant="danger" className="py-2 mb-2">{kayitHatasi}</Alert>}
            <div className="d-flex gap-2 mb-2 flex-wrap">
              <Form.Control
                size="sm"
                placeholder="Ad"
                value={formData.ad}
                onChange={e => setFormData(p => ({ ...p, ad: e.target.value }))}
                style={{ maxWidth: '180px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}
              />
              <Form.Control
                size="sm"
                placeholder="Soyad"
                value={formData.soyad}
                onChange={e => setFormData(p => ({ ...p, soyad: e.target.value }))}
                style={{ maxWidth: '180px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}
              />
              <Form.Control
                size="sm"
                placeholder="Hedef Kilo (kg)"
                type="number"
                value={formData.hedef_kilo}
                onChange={e => setFormData(p => ({ ...p, hedef_kilo: e.target.value }))}
                style={{ maxWidth: '140px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}
              />
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Form.Control
                size="sm"
                placeholder="E-posta"
                type="email"
                value={formData.eposta}
                onChange={e => setFormData(p => ({ ...p, eposta: e.target.value }))}
                style={{ maxWidth: '220px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}
              />
              <Form.Control
                size="sm"
                placeholder="Telefon"
                value={formData.telefon}
                onChange={e => setFormData(p => ({ ...p, telefon: e.target.value }))}
                style={{ maxWidth: '160px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}
              />
            </div>
            <div className="d-flex gap-2 mt-3">
              <Button size="sm" onClick={handleKaydet} disabled={kayitYukleniyor} style={{ backgroundColor: 'var(--accent-primary)', border: 'none', color: '#fff' }}>
                {kayitYukleniyor ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleIptal}>İptal</Button>
            </div>
          </div>
        ) : (
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <h2 className="display-5 fw-bolder m-0" style={{ color: 'var(--text-main)', letterSpacing: '-1.5px' }}>{danisan.ad} {danisan.soyad}</h2>
              <button
                onClick={() => setDuzenlemeModu(true)}
                title="Düzenle"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', lineHeight: 1 }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>edit</span>
              </button>
            </div>
            <div className="d-flex gap-4 mt-2 flex-wrap">
              <div className="d-flex align-items-center gap-2" style={{ color: 'var(--text-muted)' }}><span className="material-symbols-outlined" style={{ fontSize: '16px' }}>mail</span> {danisan.eposta || '-'}</div>
              <div className="d-flex align-items-center gap-2" style={{ color: 'var(--text-muted)' }}><span className="material-symbols-outlined" style={{ fontSize: '16px' }}>call</span> {danisan.telefon || '-'}</div>
              {danisan.paket_adi && (
                <div className="d-flex align-items-center gap-2" style={{ color: 'var(--accent-primary)' }}><span className="material-symbols-outlined" style={{ fontSize: '16px' }}>inventory_2</span> {danisan.paket_adi}</div>
              )}
            </div>
          </div>
        )}

        <div className="d-flex gap-4 text-end">
          {danisan.toplam_seans > 0 && (
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Seans</div>
              <div className="fw-bolder" style={{ color: (danisan.toplam_seans - (danisan.kullanilan_seans || 0)) <= 0 ? '#ef4444' : 'var(--accent-primary)', fontSize: '2rem' }}>
                {danisan.kullanilan_seans || 0}<span style={{ fontSize: '1rem', fontWeight: '500', color: 'var(--text-muted)' }}>/{danisan.toplam_seans}</span>
              </div>
            </div>
          )}
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Hedef Kilo</div>
            <div className="fw-bolder" style={{ color: 'var(--accent-tertiary)', fontSize: '2rem' }}>{danisan.hedef_kilo || '-'} <span style={{ fontSize: '1rem', fontWeight: '500' }}>kg</span></div>
          </div>
        </div>
      </div>

      {/* ÖLÇÜM DASHBOARD */}
      {stats && (
        <>
          {/* Stat Kartları */}
          <Row className="g-3 mb-4">
            <Col xs={6} lg>
              <StatCard icon="monitor_weight" label="Kilo" value={stats.kilo.son} unit="kg" diff={stats.kilo.fark} color={CHART_COLORS.kilo} />
            </Col>
            <Col xs={6} lg>
              <StatCard icon="straighten" label="Göğüs" value={stats.gogus.son} unit="cm" diff={stats.gogus.fark} color={CHART_COLORS.gogus} />
            </Col>
            <Col xs={6} lg>
              <StatCard icon="straighten" label="Bel" value={stats.bel.son} unit="cm" diff={stats.bel.fark} color={CHART_COLORS.bel} />
            </Col>
            <Col xs={6} lg>
              <StatCard icon="straighten" label="Kalça" value={stats.kalca.son} unit="cm" diff={stats.kalca.fark} color={CHART_COLORS.kalca} />
            </Col>
            <Col xs={6} lg>
              <StatCard icon="analytics" label="VKİ" value={stats.vki.son} unit="" diff={stats.vki.fark} color={CHART_COLORS.vki} />
            </Col>
          </Row>

          {/* Grafikler */}
          {chartData.length >= 2 && (
            <Row className="g-4 mb-4">
              {/* Kilo Grafiği */}
              <Col md={6}>
                <div className="p-4 rounded-4" style={{ backgroundColor: 'var(--bg-secondary)', boxShadow: 'var(--card-shadow)' }}>
                  <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                    <span className="material-symbols-outlined me-2" style={{ fontSize: '1.1rem', color: CHART_COLORS.kilo, verticalAlign: 'middle' }}>monitoring</span>
                    Kilo Değişimi
                  </h6>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="kiloGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.kilo} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={CHART_COLORS.kilo} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="tarih" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={customTooltipStyle} />
                      <Area type="monotone" dataKey="kilo" stroke={CHART_COLORS.kilo} strokeWidth={2} fill="url(#kiloGrad)" name="Kilo (kg)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Col>

              {/* Vücut Ölçüleri Grafiği */}
              <Col md={6}>
                <div className="p-4 rounded-4" style={{ backgroundColor: 'var(--bg-secondary)', boxShadow: 'var(--card-shadow)' }}>
                  <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
                    <span className="material-symbols-outlined me-2" style={{ fontSize: '1.1rem', color: CHART_COLORS.bel, verticalAlign: 'middle' }}>straighten</span>
                    Vücut Ölçüleri
                  </h6>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="tarih" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={customTooltipStyle} />
                      <Line type="monotone" dataKey="gogus" stroke={CHART_COLORS.gogus} strokeWidth={2} dot={{ r: 4 }} name="Göğüs (cm)" connectNulls />
                      <Line type="monotone" dataKey="bel" stroke={CHART_COLORS.bel} strokeWidth={2} dot={{ r: 4 }} name="Bel (cm)" connectNulls />
                      <Line type="monotone" dataKey="kalca" stroke={CHART_COLORS.kalca} strokeWidth={2} dot={{ r: 4 }} name="Kalça (cm)" connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="d-flex justify-content-center gap-3 mt-2">
                    {[{ label: 'Göğüs', color: CHART_COLORS.gogus }, { label: 'Bel', color: CHART_COLORS.bel }, { label: 'Kalça', color: CHART_COLORS.kalca }].map(l => (
                      <div key={l.label} className="d-flex align-items-center gap-1">
                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: l.color }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </>
      )}
      
      {/* Alt Bölümler — Randevu ve Ölçüm Geçmişi */}
      <Row className="g-4">
        {/* Randevu Geçmişi */}
        <Col md={6}>
          <div className="h-100 p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1.25rem', boxShadow: 'var(--card-shadow)' }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
               <h4 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Randevu Geçmişi</h4>
               <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)' }}>history</span>
            </div>
            
            <table className="w-100 text-start" style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {randevular.length > 0 ? randevular.map(r => (
                  <tr key={r.id} className="border-bottom" style={{ borderColor: 'var(--bg-tertiary)' }}>
                    <td className="py-3">
                      <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '0.9rem' }}>{new Date(r.tarih_saat).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{r.konu || 'Konu belirtilmemiş'}</div>
                    </td>
                    <td className="py-3 text-end">
                      <Badge bg="transparent" className="px-3 py-2 rounded-pill me-2" style={{ color: r.ucret_alindi ? 'var(--accent-primary)' : 'var(--accent-tertiary)', backgroundColor: r.ucret_alindi ? 'rgba(38, 109, 0, 0.1)' : 'rgba(160, 53, 115, 0.1)' }}>
                        {r.ucret_alindi ? "Ödendi" : "Ödenmedi"}
                      </Badge>
                      <Link to={`/randevu/${r.id}`} className="btn btn-sm btn-light fw-bold" style={{ color: 'var(--text-main)', backgroundColor: 'var(--bg-tertiary)', border: 'none' }}>
                        Detay
                      </Link>
                    </td>
                  </tr>
                )) : <tr><td className="py-3 text-center" style={{ color: 'var(--text-muted)' }}>Kayıt yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </Col>

        {/* Ölçüm Geçmişi Tablosu */}
        <Col md={6}>
          <div className="h-100 p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1.25rem', boxShadow: 'var(--card-shadow)' }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
               <h4 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Ölçüm Geçmişi</h4>
               <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)' }}>query_stats</span>
            </div>
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="w-100 text-start" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                    <th className="pb-3 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Tarih</th>
                    <th className="pb-3 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Kilo</th>
                    <th className="pb-3 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Göğüs</th>
                    <th className="pb-3 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Bel</th>
                    <th className="pb-3 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Kalça</th>
                    <th className="pb-3 text-uppercase text-end" style={{ fontSize: '0.7rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>VKİ</th>
                  </tr>
                </thead>
                <tbody>
                  {takipGecmisi.map(t => (
                    <tr key={t.id} className="border-bottom" style={{ borderColor: 'var(--bg-tertiary)' }}>
                      <td className="py-2 fw-bold" style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>{new Date(t.tarih).toLocaleDateString('tr-TR')}</td>
                      <td className="py-2" style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>{t.kilo || '—'}</td>
                      <td className="py-2" style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>{t.gogus_cevresi || '—'}</td>
                      <td className="py-2" style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>{t.bel_cevresi || '—'}</td>
                      <td className="py-2" style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>{t.kalca_cevresi || '—'}</td>
                      <td className="py-2 text-end" style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t.vki || '—'}</td>
                    </tr>
                  ))}
                  {takipGecmisi.length === 0 && (
                    <tr><td colSpan={6} className="py-3 text-center" style={{ color: 'var(--text-muted)' }}>Henüz ölçüm kaydı yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default DanisanDetay;